import { theme } from 'antd';
import { CreditCardIcon, EditIcon } from 'assets/images/svg';
import { useManagePermittedRoles } from 'library/hooks';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCredentialByID } from 'reduxStore/credentials/selectors';
import { credentialsActions } from 'reduxStore/credentials/slice';
import { Avatar, Button, Flex, Input, Tooltip, Typography } from 'zui/Antd';

const { Text, Title } = Typography;

const EditDrawerHeader = ({ credentialID }) => {
	const {
		token: { paddingLG, colorTextSecondary, fontWeightStrong, fontSizeSM, colorError }
	} = theme.useToken();

	const dispatch = useDispatch();
	const inputRef = useRef(null);
	const formColRef = useRef(null);

	const canUpdateCredential = useManagePermittedRoles('updateCredential');
	const permissionDeniedMessage = "You don't have permission to perform this action.";

	const credential = useSelector((state) => selectCredentialByID(state, credentialID));
	const name = credential?.name || 'Untitled Credential';
	const value = credential?.value || '-';

	const [editable, setEditable] = useState(false);
	const [updatedName, setUpdatedName] = useState(name);
	const [error, setError] = useState('');

	// Reset when credential changes
	useEffect(() => {
		setUpdatedName(name);
		setEditable(false);
		setError('');
	}, [credentialID, name]);

	// Autofocus
	useEffect(() => {
		if (editable && inputRef.current) {
			inputRef.current.focus();
		}
	}, [editable]);

	const handleFinish = useCallback(() => {
		const trimmedName = updatedName.trim();

		if (!trimmedName) {
			setError('Name is required');
			return;
		}

		setError('');

		if (trimmedName !== name.trim()) {
			dispatch(credentialsActions.updateCredential({ credentialID, data: { name: trimmedName } }));
		}

		setEditable(false);
	}, [updatedName, name, credentialID, dispatch]);

	// Save on outside click
	useEffect(() => {
		const handleClickOutside = (event) => {
			const clickedOutside = formColRef.current && !formColRef.current.contains(event.target);
			if (editable && clickedOutside && canUpdateCredential) {
				handleFinish();
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [editable, handleFinish, canUpdateCredential]);

	return (
		<Flex gap={paddingLG}>
			<Avatar
				size={54}
				icon={<CreditCardIcon color='white' width={42} height={42} />}
				style={{ backgroundColor: '#F9A052', marginTop: 6 }}
				shape='square'
			/>

			<Flex vertical flex='1' ref={formColRef} gap={4}>
				<Text type='secondary' style={{ fontSize: 12, textTransform: 'uppercase' }}>
					Credential Number
				</Text>

				<Title level={3} style={{ margin: 0 }}>
					{value}
				</Title>

				{editable ? (
					<>
						<Input
							ref={inputRef}
							value={updatedName}
							onPressEnter={handleFinish}
							onChange={(e) => setUpdatedName(e.target.value)}
							placeholder='Credential name'
							style={{
								color: colorTextSecondary,
								padding: 0
							}}
						/>
						{error && (
							<Text
								style={{
									color: colorError,
									fontSize: fontSizeSM,
									fontWeight: fontWeightStrong
								}}
							>
								{error}
							</Text>
						)}
					</>
				) : (
					<Text style={{ color: colorTextSecondary }}>{updatedName}</Text>
				)}
			</Flex>

			<Tooltip title={!canUpdateCredential ? permissionDeniedMessage : ''}>
				<span>
					<Button
						onClick={() => canUpdateCredential && setEditable((prev) => !prev)}
						type='text'
						disabled={!canUpdateCredential}
						icon={<EditIcon color='currentColor' />}
						style={{ alignSelf: 'start' }}
					/>
				</span>
			</Tooltip>
		</Flex>
	);
};

export default EditDrawerHeader;
